/**
 * PhaseProgressSection - Collapsible progress display for Flow Coach
 *
 * Shows:
 * - Phase completion percentage (circular ring)
 * - Entity checklist with status indicators
 * - Next phase preview (when >70% complete)
 * - Entity click handlers for quick navigation
 */

import { useState } from 'react'
import { ChevronDown, CheckCircle2, Circle, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ProgressRing } from './ProgressRing'
import type { PhaseEntityCard } from '@/hooks/useFlowWorkflow'
import type { JourneyPhaseWithMode } from '@ispot/shared'

// ============================================================================
// TYPES
// ============================================================================

interface PhaseProgressSectionProps {
  phase: JourneyPhaseWithMode
  progress: number // 0-100
  entityCards: PhaseEntityCard[]
  nextPhase?: { name: string; entityCount: number }
  onEntityClick: (entityType: string) => void
}

interface EntityChecklistItemProps {
  card: PhaseEntityCard
  onClick: () => void
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function PhaseProgressSection({
  phase,
  progress,
  entityCards,
  nextPhase,
  onEntityClick,
}: PhaseProgressSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  // Calculate counts
  const filledCount = entityCards.filter(
    card => card.isRequired && card.currentCount >= card.minCount
  ).length
  const totalCount = entityCards.filter(card => card.isRequired).length

  return (
    <div className="border-b border-warm-200">
      {/* Header - Always Visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-warm-50 transition-colors"
      >
        <ProgressRing progress={progress} size={36} strokeWidth={3} />
        <div className="flex-1 text-left">
          <p className="text-xs font-medium text-text">Phase Progress</p>
          <p className="text-[10px] text-warm-500">
            {filledCount}/{totalCount} entities complete
          </p>
        </div>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-warm-500 transition-transform',
            isExpanded && 'rotate-180'
          )}
        />
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-3">
          {/* Entity Checklist */}
          <div className="space-y-1.5">
            {entityCards.map(card => (
              <EntityChecklistItem
                key={card.classUri}
                card={card}
                onClick={() => onEntityClick(card.className)}
              />
            ))}
          </div>

          {/* Next Phase Preview (when >70% complete) */}
          {progress > 70 && nextPhase && (
            <div className="mt-3 p-2 bg-solution/10 border border-solution/30 rounded-lg">
              <p className="text-xs font-medium text-solution">Next Phase Preview</p>
              <p className="text-[10px] text-warm-600 mt-0.5">
                {nextPhase.name} ({nextPhase.entityCount} entity types)
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// CHECKLIST ITEM
// ============================================================================

function EntityChecklistItem({ card, onClick }: EntityChecklistItemProps) {
  // Determine status
  const isFilled = card.currentCount >= card.minCount
  const isPartial = card.currentCount > 0 && card.currentCount < card.minCount
  const isEmpty = card.currentCount === 0

  // Icon and color
  const StatusIcon = isEmpty
    ? Circle
    : isPartial
      ? AlertCircle
      : CheckCircle2

  const iconColor = isEmpty
    ? 'text-warm-400'
    : isPartial
      ? 'text-warning'
      : 'text-success'

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-warm-100 transition-colors text-left group"
    >
      <StatusIcon className={cn('w-3.5 h-3.5 flex-shrink-0', iconColor)} />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-text truncate group-hover:text-business">
          {card.className}
        </p>
        {card.isRequired && (
          <p className="text-[10px] text-warm-500">
            {card.currentCount}/{card.minCount} required
          </p>
        )}
        {!card.isRequired && (
          <p className="text-[10px] text-warm-400">
            {card.currentCount} optional
          </p>
        )}
      </div>
      {/* Count Badge */}
      <div
        className={cn(
          'px-1.5 py-0.5 rounded text-[10px] font-medium',
          isFilled ? 'bg-success/10 text-success' : 'bg-warm-200 text-warm-600'
        )}
      >
        {card.currentCount}
      </div>
    </button>
  )
}
